const STORAGE_KEY = 'crewline-tenant';
const SLUG_RE = /^[a-z0-9-]{1,63}$/i;

/** Tenant named in the URL: `/login/<slug>` (canonical) or `?tenant=<slug>` (legacy, redirected to the path form). */
export function tenantSlugFromUrl(): string | null {
  try {
    const path = /^\/login\/([^/?#]+)/i.exec(window.location.pathname)?.[1];
    if (path && SLUG_RE.test(path)) return path.toLowerCase();
    const q = new URLSearchParams(window.location.search).get('tenant');
    if (q && SLUG_RE.test(q)) return q.toLowerCase();
  } catch {
    /* ignore */
  }
  return null;
}

const RESERVED_LABELS = new Set(['www', 'app', 'admin', 'api', 'portal', 'login']);
const PLATFORM_HOST_RE =
  /(\.azurestaticapps\.net|\.azurewebsites\.net|\.azurefd\.net|\.cloudfront\.net|\.netlify\.app|\.vercel\.app|\.pages\.dev)$/i;

/** A host that can name a tenant: not localhost, an IP address or a platform default hostname (whose first label is random). */
export function isCustomHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    !!host &&
    host !== 'localhost' &&
    !/^[\d.]+$/.test(host) &&
    !host.includes(':') &&
    !PLATFORM_HOST_RE.test(host)
  );
}

// ---- sign-in domains -------------------------------------------------------------------------------------------
// Operators can attach hostnames to a tenant in the console (siemens-energy.solvent.plus → tenant "siemens").
// The app asks the API once per host, before the first render, and remembers the answer for a while so the
// lookup does not run on every page load. An unmapped host falls back to its first label.

const HOST_CACHE_PREFIX = 'crewline-host-tenant:';
const HOST_CACHE_TTL_MS = 10 * 60_000;
const HOST_LOOKUP_TIMEOUT_MS = 2_500;

interface HostMapping {
  host: string;
  /** The tenant slug, or null when the API said no tenant owns this host. */
  slug: string | null;
}

let hostMapping: HostMapping | null = null;

function readHostCache(host: string): HostMapping | null {
  try {
    const raw = localStorage.getItem(HOST_CACHE_PREFIX + host);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { slug: string | null; at: number };
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > HOST_CACHE_TTL_MS) return null;
    return {
      host,
      slug:
        typeof parsed.slug === 'string' && SLUG_RE.test(parsed.slug)
          ? parsed.slug.toLowerCase()
          : null,
    };
  } catch {
    return null;
  }
}

function writeHostCache(host: string, slug: string | null): void {
  try {
    localStorage.setItem(HOST_CACHE_PREFIX + host, JSON.stringify({ slug, at: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * Ask the API which tenant this host belongs to (GET /api/v1/tenant/resolve). Resolves quickly on
 * localhost and platform hosts, on a cached answer, on a 404, on a timeout and when offline; the app renders
 * either way and `tenantSlugFromHost()` reflects whatever was learned.
 */
export async function primeHostTenant(apiBase: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const host = window.location.hostname.toLowerCase();
  if (!isCustomHost(host)) return;
  const cached = readHostCache(host);
  if (cached) {
    hostMapping = cached;
    return;
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HOST_LOOKUP_TIMEOUT_MS);
  try {
    const res = await fetch(`${apiBase}/api/v1/tenant/resolve?host=${encodeURIComponent(host)}`, {
      signal: ctrl.signal,
      headers: { Accept: 'application/json' },
    });
    if (res.status === 404) {
      hostMapping = { host, slug: null };
      writeHostCache(host, null);
    } else if (res.ok) {
      const data = (await res.json()) as { slug?: unknown };
      if (typeof data.slug === 'string' && SLUG_RE.test(data.slug)) {
        hostMapping = { host, slug: data.slug.toLowerCase() };
        writeHostCache(host, hostMapping.slug);
      }
    }
  } catch {
    /* offline or slow: fall back to the host label */
  } finally {
    clearTimeout(timer);
  }
}

/** Test hook: pretend the API answered for this host. */
export function setHostTenantForTests(host: string, slug: string | null): void {
  hostMapping = { host: host.toLowerCase(), slug };
}

/**
 * Tenant named by the host. A sign-in domain configured in the console wins (`siemens-energy.solvent.plus` →
 * `siemens`); otherwise the first label of a custom domain with three or more labels (`siemens.solvent.plus`
 * → `siemens`). Localhost, IP addresses and platform default hostnames never count.
 */
export function tenantSlugFromHost(
  hostname = typeof window === 'undefined' ? '' : window.location.hostname,
): string | null {
  const host = hostname.toLowerCase();
  if (!isCustomHost(host)) return null;
  if (hostMapping && hostMapping.host === host && hostMapping.slug) return hostMapping.slug;
  const labels = host.split('.');
  if (labels.length < 3) return null;
  const first = labels[0] ?? '';
  if (!SLUG_RE.test(first) || RESERVED_LABELS.has(first)) return null;
  return first;
}

/**
 * Resolve which tenant this browser session belongs to.
 * Order: /login/<slug> or ?tenant= in the URL → the tenant host (a configured sign-in domain, else
 * siemens.solvent.plus → siemens) → localStorage → VITE_DEFAULT_TENANT → 'demo'. The API also accepts an
 * X-Tenant header, which the client sends on every request.
 */
export function resolveTenantSlug(): string {
  const fromUrl = tenantSlugFromUrl() ?? tenantSlugFromHost();
  if (fromUrl) {
    setTenantSlug(fromUrl);
    return fromUrl;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return (import.meta.env.VITE_DEFAULT_TENANT as string | undefined) || 'demo';
}

/** True when this browser has an explicitly chosen tenant (URL or stored), i.e. switching makes sense. */
export function hasStoredTenantSlug(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export function setTenantSlug(slug: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* ignore */
  }
}

export function clearTenantSlug(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Sign-in URL for a tenant on this deployment, e.g. https://app.example.com/login/siemens */
export function loginUrlFor(slug: string): string {
  return `${window.location.origin}/login/${encodeURIComponent(slug)}`;
}
